// library
import { useContext, useMemo, useState } from 'react'
import { useRouter } from 'next/router'
import { useDispatch } from 'react-redux'
import { Spinner } from 'react-bootstrap'
import _isEmpty from 'lodash/isEmpty'
import _flatten from 'lodash/flatten'
import Button from 'react-bootstrap/Button'
// our component library
import { Button as CLButton } from 'component_library/1_Atoms'
// constants
import {
  RudderstackIntegrations,
  sessionStorageKeys,
  dateFormats,
} from 'constants/common'
import { RudderEvent } from 'constants/rudderEvents'
import { initialBookingHelperState } from 'state/booking/constants'
import { SlotAvailabilityType } from 'app/Bookings/constants'
import { LabelAppointmentMapping, AvailabilityFilterValues } from '../constants'
// datasource
import { ProviderFilterTypes } from 'dataSource/ProviderSearch'
// context
import { TimeZoneContext } from 'context/common/TimeZoneContext'
//utils
import logEvent from 'utils/logEvent'
import { getParsedQuery, modifyURLQuery } from 'utils/common'
import { validateAndProceedBooking } from 'app/Bookings/utils'
import { getSelectedPaymentType } from 'app/ProviderSearch/rudderEventUtils'
import { getBookingQueryObject } from 'app/ProviderSearch/utils'
import { getPreviousRouterUrlQuery } from 'app/ProviderDetails/utils'
import { isNextOpeningSlotToday } from './utils'
// actions
import {
  cleanBookingInfo,
  startBooking,
  updateBookingDetails,
} from 'state/booking/actions'
// hooks
import useThriveAuth from 'hooks/useThriveAuth'
import useBreakpoint from 'hooks/useBreakPoint'
import useAppSelector from 'hooks/useAppSelector'
// components
import SlotGrid from 'common/SlotGrid'
import PageLoader from 'common/PageLoader'
import SlotGridModal from 'common/SlotGridModal'
import EmptySlots from '../EmptySlots'
import ListCardProviderSection from './ListCardProviderSection'
import MobileListCardProviderSection from './MobileListCardProviderSection'
// css
import styles from './index.module.scss'
import { utcToZonedTime } from 'date-fns-tz'
import { storeBookingDetailsForV2 } from '@app/bookings/utils/bookingHelper'

type ListCardProps = {
  showDistance: boolean
  groupedFilters: GroupedProviderFilter
  slotLocation?: string
  providerCardDetails: {
    dates: Date[]
    areSlotsEmpty: boolean
    nextAvailabilitySlot: ProviderSlot
    areSlotsLoading: boolean
    error: Error
    count: number
    providerDetails: Provider
    generatedSlots: DateSlot
  }
  renderCustomInsuranceSection?: () => JSX.Element
}

const ListCard: React.FC<ListCardProps> = ({
  slotLocation,
  providerCardDetails,
  groupedFilters,
  renderCustomInsuranceSection,
}) => {
  const router = useRouter()
  const {
    isAuthenticated,
    user: userDetails,
    loginWithRedirect,
    isThirdPartyAgent,
  } = useThriveAuth()
  const formattedRouterQuery = getParsedQuery(router)
  const {
    generatedSlots,
    dates,
    error,
    count,
    areSlotsLoading,
    areSlotsEmpty,
    nextAvailabilitySlot,
    providerDetails,
  } = providerCardDetails
  const { id, firstName, lastName, practice } = providerDetails

  //Redux Action
  const dispatch = useDispatch()
  const { isMediumSize, isLargeSize, isSmallSize } = useBreakpoint()

  // ==================================== states and const declaration

  const { dbUser: user, userEmail, b2bUserDetails } = useAppSelector(
    (state) => state.bookingHelper
  )

  const { timeZone } = useContext(TimeZoneContext)

  // ============================================== functions

  const userSelectedAvailType = groupedFilters[
    ProviderFilterTypes.AVAILABILITY_TYPE
  ]?.filter((filter) => {
    if (
      formattedRouterQuery?.availabilityType &&
      filter?.id === formattedRouterQuery?.availabilityType[0]
    ) {
      return filter
    }
  })

  const changeDate = () => {
    const nextDate = utcToZonedTime(nextAvailabilitySlot?.start, timeZone)
    nextDate.setHours(0, 0, 0, 0)
    sessionStorage.setItem(
      sessionStorageKeys.DISABLE_PROVIDER_FETCH_ONCE,
      'true'
    )
    modifyURLQuery(router, {
      slotStartDate: nextDate.getTime().toString(),
    })
  }

  const { format } = useContext(TimeZoneContext)

  const flattenedCounselingType =
    typeof formattedRouterQuery?.counselingType === 'string'
      ? formattedRouterQuery?.counselingType
      : formattedRouterQuery?.counselingType?.[0]
  const counselingTypes = groupedFilters[ProviderFilterTypes.COUNSELING_TYPE]
  const counselingTypeValue = counselingTypes.find((type) => {
    return type.id === flattenedCounselingType
  })

  const bookAppointment = (
    selectedSlot: SelectedSlot,
    button_class = '',
    logNextOpeningRudderEvent = false
  ) => {
    //initial appointment mode selection based on user's selection or default to undefined if nothing is selected
    const appointmentMode =
      userSelectedAvailType?.length === 1
        ? userSelectedAvailType[0]?.value === AvailabilityFilterValues.inPerson
          ? SlotAvailabilityType.INPERSON
          : SlotAvailabilityType.ONLINE
        : undefined

    let selectedSlotAppointmentType: SlotAvailabilityType = null

    if (selectedSlot.slot.availabilityType.length === 1) {
      selectedSlotAppointmentType =
        selectedSlot.slot.availabilityType[0] === SlotAvailabilityType.ONLINE
          ? SlotAvailabilityType.ONLINE
          : SlotAvailabilityType.INPERSON
    } else {
      selectedSlotAppointmentType = SlotAvailabilityType.ONLINE //default to video visit if none selected/ both selected
    }

    const availTypeToAddInRouterUrl = !appointmentMode
      ? selectedSlotAppointmentType
      : appointmentMode
    const mappedAvailability = Object?.keys(LabelAppointmentMapping)?.find(
      (availabilityObject) => {
        return (
          LabelAppointmentMapping[availabilityObject] ===
          availTypeToAddInRouterUrl
        )
      }
    )
    const matchedAvailabilityFilter = groupedFilters[
      ProviderFilterTypes.AVAILABILITY_TYPE
    ]?.find((filter) => {
      return filter?.value === mappedAvailability
    })

    dispatch(cleanBookingInfo(isThirdPartyAgent))
    dispatch(
      startBooking({
        ...initialBookingHelperState,
        ...(!!b2bUserDetails && { b2bUserDetails }),
        selectedSlot,
        providerDetails,
        filters: {
          slotAvailabilityTypes: selectedSlot.slot.availabilityType,
        },
        ...(!!user && { dbUser: user, userEmail: user.email }),
        ...(!!userEmail && { userEmail }), //userEmail will be available if redirecting from Support dashboard.
      })
    )
    dispatch(
      updateBookingDetails({
        providerId: providerDetails.id,
        ...(formattedRouterQuery?.counselingType && {
          counselingTypeId: formattedRouterQuery.counselingType[0] as string,
        }),
        counselingType: counselingTypeValue ? counselingTypeValue.value : '',
        ...(!appointmentMode
          ? { appointmentMode: selectedSlotAppointmentType }
          : { appointmentMode }),
      })
    )

    //-- NewBooking flow
    // We choose the first index because only one value can be selected for availabilityType and counselingType in the search page UI
    // Its an array because initially the system was designed to accept an array for the filters
    const appointmentModeId = formattedRouterQuery?.availabilityType?.[0]
    const counselingTypeId = formattedRouterQuery?.counselingType?.[0]

    storeBookingDetailsForV2({
      selectedSlot,
      providerDetails,
      appointmentModeId,
      counselingTypeId,
    })

    if (logNextOpeningRudderEvent) {
      logEvent(RudderEvent.BOOKING_NEXT_OPENING_CLICKED, {
        provider_id: providerDetails.id,
        provider_name: `${providerDetails.firstName} ${providerDetails.lastName}`,
        practice_state: practice?.state,
        practice_city: practice?.city,
        slot_timestamp: selectedSlot.slot?.start,
        button_class: button_class,
        payment_type_filter: getSelectedPaymentType(
          getPreviousRouterUrlQuery()
        ),
      })
    }

    // since provider slot is not selected manually hence we dont log PROVIDER_SLOT_SELECTED event
    // when BOOKING_NEXT_OPENING_CLICKED is triggered
    if (!logNextOpeningRudderEvent && !areSlotsEmpty) {
      logEvent(
        RudderEvent.PROVIDER_SLOT_SELECTED,
        {
          provider_name: `${firstName} ${lastName}`,
          provider_id: id,
          practice_state: practice?.state,
          practice_city: practice?.city,
          total_slots_count: count,
          slot_timestamp: selectedSlot.slot?.start,
          account_id: userDetails?.id,
          ...(slotLocation && { slot_location: slotLocation }),
        },
        {
          [RudderstackIntegrations.GOOGLE_ADS]: true,
          [RudderstackIntegrations.FACEBOOK_PIXEL]: true,
        }
      )
    }

    const { query } = router

    const queryObject = {
      ...query,
      ...(matchedAvailabilityFilter && {
        availabilityType: matchedAvailabilityFilter.id,
      }),
    }
    validateAndProceedBooking(
      isAuthenticated,
      loginWithRedirect,
      router,
      getBookingQueryObject(queryObject, selectedSlot.slot.id, timeZone)
    )
  }

  const [showModal, setShowModal] = useState<boolean>(false)

  const handleShowModal = () => {
    setShowModal(true)
  }

  const nextAvailabilitySlotObj = useMemo(() => {
    const firstAvailableDateSlot = _flatten(Object.values(generatedSlots)).find(
      (date) => !!date
    )

    return firstAvailableDateSlot || nextAvailabilitySlot
  }, [generatedSlots])

  return (
    <div className={styles.Card}>
      <SlotGridModal
        showModal={showModal}
        onHide={() => setShowModal(false)}
        providerDetails={providerDetails}
        userSelectedAvailType={userSelectedAvailType}
        groupedFilters={groupedFilters}
        slotLocation={slotLocation}
      />
      <div className={styles.Card_subDiv}>
        {isSmallSize ? (
          <MobileListCardProviderSection
            practice={practice}
            providerDetails={providerDetails}
            areSlotsLoading={areSlotsLoading}
            generatedSlots={generatedSlots}
            nextAvailabilitySlot={nextAvailabilitySlot}
            showModal={handleShowModal}
            onNextOpeningClick={bookAppointment}
            renderCustomInsuranceSection={renderCustomInsuranceSection}
          />
        ) : (
          <>
            <ListCardProviderSection
              providerDetails={providerDetails}
              practice={practice}
              renderCustomInsuranceSection={renderCustomInsuranceSection}
            />
            {isMediumSize || isLargeSize ? (
              <div className={styles.Card_subDiv_nextOpening}>
                {(nextAvailabilitySlotObj || areSlotsLoading) && (
                  <CLButton
                    theme="v2"
                    variant="primary"
                    full
                    aria-label="Select next available opening slot"
                    onClick={(event) =>
                      bookAppointment(
                        {
                          slot: nextAvailabilitySlotObj,
                          provider: providerDetails,
                        },
                        (event.currentTarget as HTMLElement).className,
                        true
                      )
                    }
                    disabled={areSlotsLoading}
                    className={styles.Card_subDiv_nextOpening_button}
                  >
                    {areSlotsLoading ? (
                      <Spinner animation="border" />
                    ) : isNextOpeningSlotToday(nextAvailabilitySlotObj) ? (
                      `Next opening: Today, ${format(
                        nextAvailabilitySlotObj?.start,
                        ` ${dateFormats.TIME_FORMAT_LOWER}`
                      ).replace(':00', '')}`
                    ) : (
                      `Next opening: ${format(
                        nextAvailabilitySlotObj?.start,
                        `${dateFormats.DAY_FORMAT} ${dateFormats.MONTH_DATE_SINGLEDIGIT_FORMAT}, ${dateFormats.TIME_FORMAT_LOWER}`
                      ).replace(':00', '')}`
                    )}
                  </CLButton>
                )}
                <Button
                  variant="link"
                  onClick={handleShowModal}
                  className={styles.view_all_availability_button}
                >
                  View all availability
                </Button>
              </div>
            ) : (
              <div
                className={`${styles.card_right_container} d-none d-lg-flex flex-column`}
              >
                <PageLoader
                  isLoading={areSlotsLoading}
                  error={error?.message}
                  loadingContainerClass={`${styles.slot_container}`}
                  isEmpty={areSlotsEmpty}
                  emptyContainerClass={styles.empty_container}
                  Components={{
                    Empty: (
                      <EmptySlots
                        providerDetails={providerDetails}
                        nextAvailabilityDate={nextAvailabilitySlotObj?.start}
                        changeDate={changeDate}
                      />
                    ),
                  }}
                  loadingMessage="" // Intentionally given so that it won't show the message
                >
                  <div className="flex-lg-grow-1">
                    <SlotGrid
                      dates={dates}
                      showSlotSelection={false}
                      providerDetails={providerDetails}
                      generatedSlots={generatedSlots}
                      showMoreButton
                      onSlotClick={bookAppointment}
                    />
                  </div>
                </PageLoader>
                <Button
                  variant="link"
                  onClick={handleShowModal}
                  className={styles.view_all_availability_button}
                >
                  View all availability
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default ListCard
